<template>
  <profile-subsection
    action-text="Update Phone Number"
    :editing="activeSection === 'Phone'"
    :disabled="activeSection && activeSection !== 'Phone'"
    :disabled-save="!canSubmit"
    @inactive-action="$emit('set-active')"
    @active-action="updatePhone"
    @cancel-action="cancel"
  >
    <template v-slot:active>
      <label
        class="hidden"
        for="phone-number"
      >
        Phone Number:
      </label>
      <input
        id="phone-number"
        :value="phone"
        class="active-phone-input"
        :disabled="submitting"
        type="tel"
        name="phone-number"
        @input="formatAndValidatePhone"
        @change="formatAndValidatePhone"
      >
      <tooltip
        placement="right"
        message="Text message alerts is an upcoming feature we’re currently working on. Add your number to be the first to know."
        classes="phone-tooltip"
      >
        <icon
          name="info"
          variant="00d8f8"
          class="phone-info-icon"
        />
      </tooltip>
      <p
        v-if="validationFailed && validationMessage"
        class="validation"
      >
        {{ validationMessage }}
      </p>
    </template>
    <template v-slot:active-disabled>
      <p class="disclaimer">
        We currently only support phone numbers based in the US and Canada.
      </p>
    </template>
    <template v-slot:inactive>
      <p class="inactive-phone-display">
        Phone Number: <span
          :class="{disabled: !phone}"
        >{{ phoneNumberDisplay }}</span>
      </p>
      <tooltip
        placement="right"
        message="Text message alerts is an upcoming feature we’re currently working on. Add your number to be the first to know."
        classes="phone-tooltip"
      >
        <icon
          name="info"
          variant="00d8f8"
          class="inactive phone-info-icon"
        />
      </tooltip>
    </template>
  </profile-subsection>
</template>

<script>
import ProfileSubsection from '@components/profile/subsection';
import Icon from '@components/singletons/icon';
import Tooltip from '@components/singletons/tooltip';

import { removePhonePrefix, interpolatePhone } from '@/utils';

export default {
  components: {ProfileSubsection, Icon, Tooltip},
  props: {
    activeSection: {
      type: String,
    },
    submitting: {
      type: Boolean,
    },
    user: {
      type: Object,
    },
  },
  data() {
    return {
      phone: '',
      prevPhone: '',
      validationFailed: false,
      validationMessage: null,
    };
  },
  computed: {
    canSubmit() {
      return this.phoneEntered && !this.validationFailed;
    },
    phoneEntered() {
      return this.phone.length > 0;
    },
    phoneNumberDisplay() {
      return this.phone || '123-456-7890';
    },
  },
  mounted() {
    let userPhone = '';
    if (this.user.phone) {
      userPhone = interpolatePhone(removePhonePrefix(this.user.phone));
    }
    this.phone = userPhone,
    this.prevPhone = userPhone;
    this.$watch('activeSection', (curr, prev) => {
      if (prev === 'Phone' && curr !== 'Phone') {
        this.validationFailed = false;
        this.validationMessage = null;
      }
    });
  },
  methods: {
    cancel() {
      this.$emit('clear-active');
      this.phone = this.prevPhone;
    },
    formatAndValidatePhone(event) {
      const value = event.currentTarget.value;
      const onlyDigits = value.replace(/-/g, '');
      this.phone = interpolatePhone(onlyDigits);
      this.validatePhone();
    },
    updatePhone() {
      this.$emit('set-submitting', true);

      const params = {
        user: {
          phone: `+1${this.phone.replace(/-/g, '')}`,
        },
      };

      // Verify we are sending phone in valid format
      // Should be +11234567890 where 1234567890 is user enter phone
      if (!/^\+1\d{10}/.test(params.user.phone)) {
        this.$emit('set-submitting', false);
        this.$notify({
          group: 'notifications',
          text: 'Error Updating Profile',
          type: 'error',
        });
        return;
      }

      this.$store.dispatch('session/updateUser', params).then(response => {
        this.$emit('clear-active');
        this.prevPhone = this.phone;
        this.$emit('set-submitting', false);
        this.$notify({
          group: 'notifications',
          text: 'Profile Updated',
          type: 'success',
        });
      }).catch(e => {
        this.phone = this.prevPhone;
        this.$emit('set-submitting', false);
        this.$notify({
          group: 'notifications',
          text: 'Error Updating Profile',
          type: 'error',
        });
      });
    },
    validatePhone() {
      if (this.phoneEntered) {
        if (/^[0-9]{3}-[0-9]{3}-[0-9]{4}$/.test(this.phone)) {
          this.validationFailed = false;
          this.validationMessage = null;
        } else {
          this.validationFailed = true;
          this.validationMessage = 'Must be valid US or Canada phone number';
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.disclaimer {
  color: #bfbfbf;
  margin: rem-calc(24) 0 0;
}

.validation {
  color: #dc3416;
  margin: 0;
  font-size: rem-calc(12);
}

.inactive-phone-display {
  span {
    @include breakpoint(small) {
      display: block;
      margin-top: rem-calc(4);
    }
  }
}

.inactive-phone-display,
.active-phone-input {
  display: inline-block !important;
}

.active-phone-input {
  @include breakpoint(small) {
    width: 80% !important;
  }
}

.phone-info-icon {
  vertical-align: middle;
  display: inline-block;
  margin-left: rem-calc(12);
  height: rem-calc(16);
  width: rem-calc(16);

  &.inactive {
    @include breakpoint(small) {
      display: none;
    }
  }
}
</style>
